import ContentIllustration from '../../components/ui/ContentIllustration';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
    KeyRound, User, ArrowRight, Loader2, AlertTriangle, CheckCircle,
    Clock, Play, Brain, Trophy, Lock, Mail, Pencil, Check, X, Save
} from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import api from '../../services/api';
import ThemeToggle from '../../components/ui/ThemeToggle';

/**
 * CategoryCode — public hub for a category_code.
 *
 * Flow:
 *   1. User arrives with /category-code?code=XXX (or enters code manually)
 *   2. Hub is fetched. If no participant_name yet → form asking for name
 *   3. Cards list (one per sujet). Click → start-sujet, then navigate to /quiz-code
 *      with a return_to param pointing back here so the child player brings them back.
 *   4. On return (after finish), hub is re-fetched → statuses update.
 */
export default function CategoryCode() {
    usePageTitle('Panel de quiz');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const urlCode = (searchParams.get('code') || '').toUpperCase();

    const [codeInput, setCodeInput] = useState(urlCode);
    const [hub, setHub] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [nameInput, setNameInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [startingSsId, setStartingSsId] = useState(null);
    const [emailEditing, setEmailEditing] = useState(false);
    const [emailDraft, setEmailDraft] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);

    const autoFetchedRef = useRef(false);

    const formatCode = (raw) => {
        // Accepte le préfixe PARC- (et l'ancien CAT-) + groupes de 4
        const clean = raw.replace(/[^A-Za-z0-9-]/g, '').toUpperCase().slice(0, 24);
        return clean;
    };

    const fetchHub = useCallback(async (rawCode) => {
        const code = rawCode.toUpperCase().trim();
        if (!code) return false;
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get('/category-code/hub.php', { params: { code } });
            if (data?.success) {
                setHub(data.data);
                return true;
            }
            setError(data?.message || 'Code invalide');
            return false;
        } catch (err) {
            setError(err.response?.data?.message || 'Code invalide');
            setHub(null);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-fetch on arrival with URL code
    useEffect(() => {
        if (urlCode && !autoFetchedRef.current) {
            autoFetchedRef.current = true;
            fetchHub(urlCode);
        }
    }, [urlCode, fetchHub]);

    // Pre-fill the optional email input with the invitation email set by
    // the owner (if any) so the participant doesn't have to retype it.
    useEffect(() => {
        if (hub?.email && !emailInput) {
            setEmailInput(hub.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hub?.email]);

    // Refresh hub when coming back from a child quiz (tab focus heuristic)
    useEffect(() => {
        if (!hub?.code) return;
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchHub(hub.code);
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [hub?.code, fetchHub]);

    const handleCheckCode = async () => {
        const clean = codeInput.replace(/-/g, '');
        if (clean.length < 10) {
            setError('Code invalide (format attendu : CAT-XXXX-XXXX-XXXX)');
            return;
        }
        const ok = await fetchHub(codeInput);
        if (ok) {
            // Put the code in URL so refresh/bookmark works
            navigate(`/category-code?code=${encodeURIComponent(codeInput.toUpperCase())}`, { replace: true });
        }
    };

    const handleSetName = async () => {
        if (!nameInput.trim() || nameInput.trim().length < 2) {
            setError('Entrez votre nom et prénom (2 caractères minimum)');
            return;
        }
        const email = emailInput.trim();
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setError('Email invalide (ou laissez vide)');
            return;
        }
        setSavingName(true);
        setError(null);
        try {
            await api.post('/category-code/set-participant.php', {
                code: hub.code,
                participant_name: nameInput.trim(),
                email: email || undefined,
            });
            await fetchHub(hub.code);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setSavingName(false);
    };

    const handleSaveEmail = async () => {
        const email = emailDraft.trim();
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setError('Email invalide (ou laissez vide pour retirer)');
            return;
        }
        setSavingEmail(true);
        setError(null);
        try {
            await api.post('/category-code/set-participant.php', {
                code: hub.code,
                participant_name: hub.participant_name,
                email: email,
            });
            setEmailEditing(false);
            await fetchHub(hub.code);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setSavingEmail(false);
    };

    const handleStartSujet = async (sousSujetId, mode = 'quiz') => {
        setStartingSsId(sousSujetId);
        setError(null);
        try {
            const { data } = await api.post('/category-code/start-sujet.php', {
                code: hub.code,
                sous_sujet_id: sousSujetId,
                mode,
            });
            const childCode = data.data?.child_code;
            if (!childCode) throw new Error('Réponse invalide');
            const returnTo = `/category-code?code=${encodeURIComponent(hub.code)}`;
            navigate(`/quiz-code?code=${encodeURIComponent(childCode)}&return_to=${encodeURIComponent(returnTo)}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur au démarrage du sujet');
            setStartingSsId(null);
        }
    };

    // =========== RENDER ===========

    // Step 0 : no code yet
    if (!hub && !loading) {
        return (
            <PageShell title="Accès à un panel de quiz">
                <div style={{ textAlign: 'center', marginBottom: 22 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                        <ContentIllustration name="key-round" size={48} />
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: 0 }}>
                        Entrez le code que l'on vous a fourni.
                    </p>
                </div>

                <input
                    type="text"
                    value={codeInput}
                    onChange={e => { setCodeInput(formatCode(e.target.value)); setError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleCheckCode()}
                    placeholder="CAT-XXXX-XXXX-XXXX"
                    className="cc-code-input"
                    maxLength={24}
                    autoFocus
                />

                {error && (
                    <div className="cc-error"><AlertTriangle size={14} /> {error}</div>
                )}

                <button className="btn btn-primary cc-submit-btn" onClick={handleCheckCode}
                    disabled={codeInput.replace(/-/g, '').length < 10}>
                    <ArrowRight size={18} /> Voir le panel
                </button>
            </PageShell>
        );
    }

    // Loading
    if (loading && !hub) {
        return (
            <PageShell title="Chargement">
                <div className="cc-loading"><Loader2 size={28} className="spin" /> Chargement du panel...</div>
            </PageShell>
        );
    }

    // Step 1 : no participant name yet
    if (hub && !hub.participant_name) {
        return (
            <PageShell title={hub.category.label}>
                <div style={{ textAlign: 'center', marginBottom: 22 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                        <User size={28} />
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: 0 }}>
                        Vous allez accéder aux quiz de la catégorie <strong>{hub.category.label}</strong>.
                        Entrez votre nom pour commencer.
                    </p>
                </div>
                <input
                    type="text"
                    value={nameInput}
                    onChange={e => { setNameInput(e.target.value); setError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleSetName()}
                    placeholder="Votre nom et pr&eacute;nom"
                    className="cc-name-input"
                    autoFocus
                    maxLength={100}
                />
                <div style={{ marginTop: 12, position: 'relative' }}>
                    <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        type="email"
                        value={emailInput}
                        onChange={e => { setEmailInput(e.target.value); setError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleSetName()}
                        placeholder="Votre email (optionnel)"
                        className="cc-name-input"
                        style={{ paddingLeft: 40 }}
                        maxLength={255}
                    />
                </div>
                <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.4 }}>
                    {hub.participant_email
                        ? <>Email enregistr&eacute; pr&eacute;c&eacute;demment &mdash; modifiable.</>
                        : hub.email_invitation
                            ? <>Email fourni par l'inviteur &mdash; modifiable si vous souhaitez utiliser une autre adresse.</>
                            : <>Fourni uniquement pour recevoir votre score{hub.sealed && <> et &ecirc;tre pr&eacute;venu si les r&eacute;sultats sont d&eacute;verrouill&eacute;s</>}. Non partag&eacute;.</>
                    }
                </p>
                <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.4, fontStyle: 'italic' }}>
                    Vos r&eacute;sultats seront &eacute;galement transmis au fournisseur du code.
                </p>
                {error && (
                    <div className="cc-error"><AlertTriangle size={14} /> {error}</div>
                )}
                <button className="btn btn-primary cc-submit-btn" onClick={handleSetName}
                    disabled={savingName || !nameInput.trim()}>
                    {savingName ? <Loader2 size={18} className="spin" /> : <><ArrowRight size={18} /> Continuer</>}
                </button>
            </PageShell>
        );
    }

    // Step 2 : hub with cards
    if (!hub) return null;

    const bgImage = hub.category.image || hub.scope.header_image;
    const themeColor = hub.category.couleur_theme || hub.scope.couleur_theme || '#8B5CF6';

    return (
        <div
            className={`cc-hub${bgImage ? ' cc-hub--has-bg' : ''}`}
            style={{
                '--cc-accent': themeColor,
                ...(bgImage ? { '--cc-bg-image': `url(${bgImage})` } : {}),
            }}
        >
            <ThemeToggle className="cc-theme-toggle" />
            <div className="cc-hub-container">
                <header className="cc-hub-header">
                    <p className="cc-hub-kicker">Acc&egrave;s &agrave; un panel de quiz par code</p>
                    <p className="cc-hub-scope">{hub.scope.label}</p>
                    <h1 className="cc-hub-title">{hub.category.label}</h1>
                    <p className="cc-hub-subtitle">
                        Bonjour <strong>{hub.participant_name}</strong> — {hub.nb_finished} / {hub.nb_total} sujet{hub.nb_total > 1 ? 's' : ''} termin&eacute;{hub.nb_total > 1 ? 's' : ''}
                    </p>
                    <div className="cc-hub-email">
                        <Mail size={13} />
                        {emailEditing ? (
                            <>
                                <input
                                    type="email"
                                    value={emailDraft}
                                    onChange={e => setEmailDraft(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { e.preventDefault(); handleSaveEmail(); }
                                        if (e.key === 'Escape') { e.preventDefault(); setEmailEditing(false); }
                                    }}
                                    placeholder="votre@email.fr"
                                    className="cc-hub-email-input"
                                    autoFocus
                                    disabled={savingEmail}
                                    maxLength={255}
                                />
                                <button className="cc-hub-email-btn cc-hub-email-btn--save" onClick={handleSaveEmail} disabled={savingEmail} title="Enregistrer">
                                    {savingEmail ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
                                </button>
                                <button className="cc-hub-email-btn cc-hub-email-btn--cancel" onClick={() => setEmailEditing(false)} disabled={savingEmail} title="Annuler">
                                    <X size={12} />
                                </button>
                            </>
                        ) : (
                            <>
                                {hub.email
                                    ? <span className="cc-hub-email-value">{hub.email}</span>
                                    : <span className="cc-hub-email-placeholder">Ajouter un email pour recevoir les r&eacute;sultats</span>
                                }
                                {!hub.email_locked && (
                                    <button className="cc-hub-email-btn" onClick={() => { setEmailDraft(hub.email || ''); setEmailEditing(true); }} title={hub.email ? 'Modifier' : 'Ajouter'}>
                                        <Pencil size={11} />
                                    </button>
                                )}
                                {hub.email_locked && (
                                    <span className="cc-hub-email-locked" title="Adresse fournie par le formateur"><Lock size={10} /></span>
                                )}
                            </>
                        )}
                    </div>
                    {hub.nb_finished === hub.nb_total && hub.nb_total > 0 && !hub.sealed && (
                        <div className="cc-hub-finished"><Trophy size={16} /> Panel terminé</div>
                    )}
                    {hub.sealed && !hub.sealed_released && (
                        <div className="cc-hub-sealed">
                            <Lock size={16} /> Les résultats seront communiqués ultérieurement
                        </div>
                    )}
                    {hub.sealed && hub.sealed_released && (
                        <div className="cc-hub-finished">
                            <Trophy size={16} /> Résultats descellés — correction disponible
                        </div>
                    )}
                    {hub.review_expires_at && !hub.review_expired && (
                        hub.nb_total > 0 && hub.nb_finished === hub.nb_total ? (
                            <p className="cc-hub-review">
                                Correction consultable jusqu'au {new Date(hub.review_expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                        ) : (
                            <p className="cc-hub-review">
                                Chaque session restera consultable encore pendant {hub.review_window_hours || 48}h apr&egrave;s avoir termin&eacute; tous les quiz.
                            </p>
                        )
                    )}
                </header>

                {error && (
                    <div className="cc-error" style={{ marginBottom: 16 }}><AlertTriangle size={14} /> {error}</div>
                )}

                <div className="cc-card-grid">
                    {hub.cards.map((card, idx) => {
                        const isFinished = card.status === 'FINISHED';
                        const isInProgress = card.status === 'IN_PROGRESS';
                        const isSealed = hub.sealed && !hub.sealed_released;
                        const isLocked = isFinished && !card.review_available;
                        const isStarting = startingSsId === card.sous_sujet_id;
                        const pct = isFinished && card.total != null && card.total > 0 ? Math.round((card.score / card.total) * 100) : null;
                        const StatusIcon = isStarting ? Loader2
                            : (isFinished && isSealed) ? Lock
                            : isLocked ? Lock
                            : isFinished ? CheckCircle
                            : isInProgress ? Clock
                            : Play;
                        const statusText = isStarting ? 'Chargement...'
                            : (isFinished && isSealed) ? 'Scellé'
                            : isLocked ? 'Correction expirée'
                            : isFinished ? 'Terminé'
                            : isInProgress ? 'En cours'
                            : 'À faire';

                        return (
                            <button
                                key={card.sous_sujet_id}
                                className={`cc-card cc-card--${card.status.toLowerCase()}${isLocked ? ' cc-card--locked' : ''}`}
                                disabled={isStarting || isLocked}
                                onClick={() => handleStartSujet(card.sous_sujet_id, isInProgress ? (card.mode || 'quiz') : 'quiz')}
                            >
                                <div className="cc-card-head">
                                    <span className="cc-card-num">{String(idx + 1).padStart(2, '0')}</span>
                                    <span className="cc-card-badge">
                                        <StatusIcon size={13} className={isStarting ? 'spin' : ''} />
                                        {statusText}
                                    </span>
                                </div>
                                <div className="cc-card-title">{card.label}</div>
                                {card.description && <div className="cc-card-desc">{card.description}</div>}
                                <div className="cc-card-foot">
                                    <span className="cc-card-meta">
                                        {card.nb_questions} question{card.nb_questions > 1 ? 's' : ''}
                                    </span>
                                    {isFinished && pct !== null && (
                                        <span className="cc-card-score">{card.score}/{card.total} · {pct}%</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <footer className="cc-hub-footer">
                    <Link to="/">MonQuiz.fr</Link>
                    <span> — Pas de compte nécessaire</span>
                </footer>
            </div>
        </div>
    );
}

function PageShell({ title, children }) {
    return (
        <div className="public-code-page">
            <div className="public-code-bg-overlay" />
            <ThemeToggle className="cc-theme-toggle" />
            <div className="public-code-inner">
                <h1 className="public-code-title">{title}</h1>
                <div className="public-code-card">
                    {children}
                </div>
            </div>
            <footer className="public-code-footer">
                <nav className="public-code-footer-links">
                    <Link to="/mentions-legales">Mentions légales</Link>
                    <Link to="/cgv">CGV</Link>
                    <Link to="/confidentialite">Confidentialité</Link>
                    <a href="mailto:contact@monquiz.fr">Contact</a>
                </nav>
                <span className="public-code-footer-copy">© {new Date().getFullYear()} MonQuiz™ — Tous droits réservés</span>
            </footer>
        </div>
    );
}
